import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CommentDataResult,
  CommentParams,
  UpdateType
} from "interfaces/sessions";
import { getServicesManager } from '../../../../services';
import uuid from 'react-uuid';
import { RootState } from '../../../../store';
import { loadSuccess, removeCommentId, startPendingComment, updateComment } from './data-slice';

export type CommentFunctions = {
  addComment: (sessionId: string, selectedTranscriptIdx?: number, comment?: string) => void;
  saveComment: (sessionId: string, commentId: string) => Promise<void>; 
  addAndSaveComment: (sessionId: string, selectedTranscriptIdx?: number, comment?: string) => Promise<void>;
  removeComment: (sessionId: string, commentId: string) => Promise<boolean>;
  updateCommentText: (sessionId: string, commentId: string, updatedText: string) => Promise<boolean>;
  saveComments: (sessionId: string, commentsToUpdate: CommentParams[]) => Promise<(CommentDataResult | null)[] | null>;
}

export const useCommentFunctions = (): CommentFunctions => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.value);
  const sessionDetails = useSelector((state: RootState) => state.sessionDetails.sessionIdToDetails);

  const saveComments = useCallback(async (
    sessionId: string, 
    commentsToUpdate: CommentParams[]
  ): Promise<(CommentDataResult | null)[] | null> => {
    if (!sessionId) return null
    return await getServicesManager().updateComments(sessionId, commentsToUpdate);
  }, []);

  const constructNewComment = useCallback((sessionId: string, selectedTranscriptIdx?: number, comment?: string): CommentDataResult | null => {
    if (!sessionId || !sessionDetails[sessionId]?.data) return null;
    const sessionData = sessionDetails[sessionId].data;
    const turns = sessionData?.turns ?? []
    const transcriptIdx = selectedTranscriptIdx !== undefined && turns.length > selectedTranscriptIdx ? selectedTranscriptIdx : undefined
    
    const turn = transcriptIdx !== undefined && transcriptIdx >= 0 && transcriptIdx < turns.length ? turns[transcriptIdx] : null;
    
    const newComment: CommentDataResult = {
      comment_id: uuid(),
      comment: comment ?? '',
      session_id: sessionId,
      user_id: user?.user_id ?? '',
      created_at: new Date(),
      user_name: user?.user_name ?? 'You',
      start: turn?.start ?? null,
      end: turn?.end ?? null,
      transcriptIdx: transcriptIdx,
      isPending: true 
    };
    return newComment;
  }, [sessionDetails, user]);

  const saveComment = useCallback(async (sessionId: string, commentId: string) => {
    if (!sessionId || !sessionDetails[sessionId]?.data) return;
    const sessionData = sessionDetails[sessionId].data
    const comments = sessionData?.comments ?? []
    const idx = comments.findIndex((value) => value.comment_id === commentId);
    if (idx === -1) return;
    const comment = comments[idx];
    if (!comment) return;
    try {
      const updatedComment = await getServicesManager().updateComments(sessionId, [{
        comment: {
          comment: comment.comment, 
          commentId, 
          start: comment.start !== null ? comment.start : undefined, 
          end: comment.end !== null ? comment.end : undefined
        },
        update: UpdateType.ADD
      }])
      if (!updatedComment || !updatedComment[0]) return;
      dispatch(updateComment({
        sessionId,
        commentId: comment.comment_id,
        partialComment: {...updatedComment[0], isPending: false, user_name: user?.user_name ?? 'You'}
      }));
    } catch (e) {
      console.error('Error saving comment:', e);
    }
  }, [sessionDetails, dispatch, user]);


  const addComment = useCallback((sessionId: string, selectedTranscriptIdx?: number, comment?: string) => {
    const newComment = constructNewComment(sessionId, selectedTranscriptIdx, comment);
    if (!newComment) return;

    dispatch(startPendingComment({
      sessionId,
      comment: newComment
    }));
  }, [dispatch, sessionDetails, user])

  const addAndSaveComment = useCallback(async (sessionId: string, selectedTranscriptIdx?: number, comment?: string) => {
    if (!sessionId || !sessionDetails[sessionId]?.data) return;
    const newComment = constructNewComment(sessionId, selectedTranscriptIdx, comment);
    if (!newComment) return;

    dispatch(startPendingComment({
      sessionId,
      comment: newComment
    }));
    try {
      const updatedComment = await getServicesManager().updateComments(sessionId, [{
        comment: {
          comment: newComment.comment, 
          commentId: newComment.comment_id, 
          start: newComment.start !== null ? newComment.start : undefined, 
          end: newComment.end !== null ? newComment.end : undefined
        },
        update: UpdateType.ADD
      }])
      if (!updatedComment || !updatedComment[0]) throw new Error('Failed to save comment');
      dispatch(updateComment({
        sessionId,
        commentId: newComment.comment_id,
        partialComment: {...updatedComment[0], isPending: false, user_name: user?.user_name ?? 'You'}
      }));
    } catch (e) {
      console.error('Error saving comment:', e);
      dispatch(removeCommentId({
        sessionId,
        commentId: newComment.comment_id
      }))
    }
  }, [addComment, saveComments, sessionDetails, constructNewComment, dispatch, user]);

  const removeComment = useCallback(async (sessionId: string, commentId: string): Promise<boolean> => {
    if (!sessionId || !sessionDetails[sessionId]?.data?.comments) return false;
    
    const sessionData = sessionDetails[sessionId].data;
    const comments = sessionData?.comments ?? []
    const idx = comments.findIndex((value) => value.comment_id === commentId);
    if (idx === -1) return false;
    
    const comment = comments[idx];
    if (!comment) return false;
    
    // Check if the current user is the owner of the comment
    if (comment.user_id && user?.user_id && comment.user_id !== user.user_id) {
      console.warn('Permission denied: Cannot delete another user\'s comment');
      return false;
    }
    
    if (comments[idx]?.isPending) {
      dispatch(removeCommentId({
        sessionId,
        commentId
      }));
      return true;
    }
    
    try {
      await getServicesManager().updateComments(sessionId, [{
        comment: {comment: '', commentId, start: undefined, end: undefined},
        update: UpdateType.REMOVE
      }]);
      dispatch(removeCommentId({
        sessionId,
        commentId
      }));
      return true;
    } catch (e) {
      console.error('Error removing comment:', e);
      return false;
    }
  }, [dispatch, sessionDetails, user]);

  const updateCommentText = useCallback(async (sessionId: string, commentId: string, updatedText: string): Promise<boolean> => {
    if (!sessionId || !sessionDetails[sessionId]?.data?.comments) return false;
    const comment = sessionDetails[sessionId].data?.comments?.find((value) => value.comment_id === commentId);
    if (!comment) return false;
    
    // Check if the current user is the owner of the comment
    if (comment.user_id && user?.user_id && comment.user_id !== user.user_id) {
      console.warn('Permission denied: Cannot edit another user\'s comment');
      return false;
    }
    
    dispatch(updateComment({
      sessionId,
      commentId,
      partialComment: { comment: updatedText }
    }))
    
    if (comment.isPending) return true;
    
    try {
      await getServicesManager().updateComments(sessionId, [{
        comment: {comment: updatedText, commentId, start: comment.start ?? undefined, end: comment.end ?? undefined},
        update: UpdateType.MODIFY
      }])
      return true;
    } catch (e) {
      console.error('Error updating comment:', e);
      return false;
    }
  }, [dispatch, sessionDetails, user]);

  return {
    addComment,
    addAndSaveComment,
    removeComment,
    updateCommentText,
    saveComments,
    saveComment,
  };
}; 